// ForgeTrack — Classifier Tuning Dashboard
// Route: /classifier-tuning
//
// Phase B of the adaptive learning loop. Read-only dashboard surfacing
// the metrics computed by ClassifierMetricsService:
//
//   • Cost summary (total, avg per conversation, by tier)
//   • Tier hit rates (how often each cascading tier resolves)
//   • Per-project acceptance rate (where the AI is right vs wrong)
//   • Catchall work items (>= N conversations on one item — concentration risk)
//   • Schema-violation rate on Tier 4 (parser failures by conversation length)
//   • Human override rate (mapping + R&D verdict)
//
// Data source: /api/v1/classifier-tuning/metrics/summary?days=N. The
// page is initial-load-only on the summary endpoint; individual panels
// don't drill down further yet (Phase C will add proposal apply UI).

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, { GlassPanel, SectionLabel, LoadingShimmer } from '../components/engine/EnginePageShell';
import { useApiClient } from '@forgetrack/api-client';

export const Route = createFileRoute('/classifier-tuning')({
  component: ClassifierTuningPage,
});

interface TierHitRate {
  tier: string;
  count: number;
  percentage: number;
  avgCostUSD: number;
  avgLatencyMs: number | null;
}

interface TierHitRatesResult {
  totalObservations: number;
  sinceUtc: string;
  untilUtc: string;
  tiers: TierHitRate[];
  totalCostUSD: number;
  avgCostPerConversationUSD: number;
}

interface PerProjectAccuracy {
  projectId: number | null;
  projectName: string | null;
  committedCount: number;
  overriddenCount: number;
  acceptanceRate: number;
}

interface CatchallWorkItem {
  workItemId: number;
  workItemTitle: string;
  projectName: string;
  conversationCount: number;
  avgConfidence: number;
}

interface SchemaViolationStat {
  tier4TotalRuns: number;
  tier4SchemaViolations: number;
  schemaViolationRate: number;
  longConversationViolations: number;
  shortConversationViolations: number;
}

interface HumanOverrideRate {
  totalCommitted: number;
  overroOdeCount: number;
  overrideRate: number;
  rdOverroOdeCount: number;
  rdOverrideRate: number;
}

interface SummaryResponse {
  sinceUtc: string;
  untilUtc: string;
  tierHitRates: TierHitRatesResult;
  perProject: PerProjectAccuracy[];
  catchalls: CatchallWorkItem[];
  schemaViolations: SchemaViolationStat;
  overrideRate: HumanOverrideRate;
}

interface EvalProjectBreakdown {
  projectId: number;
  projectName: string;
  corpusSize: number;
  correctAtConfident: number;
  correctAtAny: number;
  precisionAtConfident: number;
  coverageAtConfident: number;
}

interface EvalSampleMiss {
  observationId: number;
  conversationTitle: string;
  tier1ProjectId: number | null;
  tier1WorkItemId: number | null;
  tier1Confidence: number;
  humanProjectId: number;
  humanWorkItemId: number;
  tierExitedAt: string;
  reasoning: string;
}

interface EvalHistoryEntry {
  id: number;
  ranAtUtc: string;
  corpusSize: number;
  bypassThreshold: number;
  tier1ConfidentPrecision: number;
  tier1ConfidentCoverage: number;
  tier1AnyPrecision: number;
  tier1AnyRecall: number;
}

interface PerPromptModelRow {
  promptKey: string;
  displayName: string;
  defaultModel: string;
  overrideModel: string | null;
  updatedAt: string | null;
  isOverride: boolean;
}

interface JobStatusRow {
  jobId: string;
  cron: string | null;
  lastExecution: string | null;
  nextExecution: string | null;
  queue: string | null;
  lastJobId: string | null;
}

interface ConfigOverrideRow {
  id: number;
  tenantId: number | null;
  configKey: string;
  configValue: string;
  valueType: string;
  appliedFromProposalId: number | null;
  updatedAt: string;
  appliedByUserId: number | null;
  notes: string | null;
}

interface KeywordRuleRow {
  id: number;
  projectId: number;
  pattern: string;
  description: string;
  isActive: boolean;
  source: string;
  createdAt: string;
  createdByUserId: number | null;
  appliedFromProposalId: number | null;
}

interface EvalRDSampleMiss {
  observationId: number;
  conversationTitle: string;
  messageCount: number;
  durationMinutes: number | null;
  aiSaidQualifies: boolean;
  humanSaidQualifies: boolean;
}

interface EvalRDVerdictMetrics {
  totalEvaluated: number;
  truePositive: number;
  trueNegative: number;
  falsePositive: number;
  falseNegative: number;
  precision: number;
  recall: number;
  f1: number;
  sampleFalseNegatives: EvalRDSampleMiss[];
  sampleFalsePositives: EvalRDSampleMiss[];
}

interface EvalSummary {
  tenantId: number;
  ranAtUtc: string;
  corpusSize: number;
  tier1ConfidentVerdicts: number;
  tier1ConfidentCorrect: number;
  tier1AnyVerdict: number;
  tier1AnyCorrect: number;
  tier1ConfidentPrecision: number;
  tier1ConfidentCoverage: number;
  tier1AnyPrecision: number;
  tier1AnyRecall: number;
  currentBypassThreshold: number;
  byProject: EvalProjectBreakdown[];
  sampleMisses: EvalSampleMiss[];
  rdVerdict: EvalRDVerdictMetrics | null;
}

interface ProposalSummary {
  id: number;
  tenantId: number | null;
  proposalType: string;
  status: string;
  title: string;
  description: string;
  projectedImpact: string | null;
  generatedAt: string;
  generatedByJob: string | null;
  appliedAt: string | null;
  rejectedAt: string | null;
  snoozedUntil: string | null;
}

const TIER_COLORS: Record<string, string> = {
  tier1: C.neon,
  tier2: C.cyan,
  tier4: C.violet,
};

const TIER_LABELS: Record<string, string> = {
  tier1: '⚡ Tier 1 (Heuristic)',
  tier2: '🧪 Tier 2 (Cheap LLM)',
  tier4: '🧠 Tier 4 (Full Pipeline)',
};

const TIER_DESCRIPTIONS: Record<string, string> = {
  tier1: 'Project regex + Jaccard + cosine embedding. ~$0.0001/conv.',
  tier2: 'Focused json_schema LLM call. ~$0.02/conv on gpt-5.5.',
  tier4: 'Full v5 pipeline (6 steps). ~$0.80/conv.',
};

function ClassifierTuningPage() {
  const { createApi } = useApiClient();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<SummaryResponse | null>(null);
  const [proposals, setProposals] = useState<ProposalSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<number | null>(null);
  const [eval_, setEval_] = useState<EvalSummary | null>(null);
  const [evalRunning, setEvalRunning] = useState(false);
  const [overrides, setOverrides] = useState<ConfigOverrideRow[]>([]);
  const [keywordRules, setKeywordRules] = useState<KeywordRuleRow[]>([]);
  const [jobStatuses, setJobStatuses] = useState<JobStatusRow[]>([]);
  const [evalHistory, setEvalHistory] = useState<EvalHistoryEntry[]>([]);
  const [perPromptModels, setPerPromptModels] = useState<PerPromptModelRow[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const api = await createApi();
      const [summaryRes, proposalsRes, overridesRes, rulesRes, jobsRes, evalHistRes, perPromptRes] = await Promise.all([
        api.get<SummaryResponse>(`/api/v1/classifier-tuning/metrics/summary?days=${days}`),
        api.get<ProposalSummary[]>(`/api/v1/classifier-tuning/proposals?status=Pending&days=90`),
        api.get<ConfigOverrideRow[]>(`/api/v1/classifier-tuning/overrides`),
        api.get<KeywordRuleRow[]>(`/api/v1/classifier-tuning/keyword-rules`),
        api.get<JobStatusRow[]>(`/api/v1/classifier-tuning/jobs/status`),
        api.get<EvalHistoryEntry[]>(`/api/v1/classifier-tuning/eval/history?take=20`),
        api.get<PerPromptModelRow[]>(`/api/v1/classifier-tuning/per-prompt-models`),
      ]);
      setData(summaryRes.data);
      setProposals(proposalsRes.data ?? []);
      setOverrides(overridesRes.data ?? []);
      setKeywordRules(rulesRes.data ?? []);
      setJobStatuses(jobsRes.data ?? []);
      setEvalHistory(evalHistRes.data ?? []);
      setPerPromptModels(perPromptRes.data ?? []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [createApi, days]);

  const triggerThresholdJob = useCallback(async () => {
    try {
      const api = await createApi();
      await api.post('/api/v1/classifier-tuning/jobs/threshold');
      // Wait briefly for the job to complete then reload.
      setTimeout(load, 3000);
    } catch (err) {
      console.error('Failed to trigger threshold job', err);
    }
  }, [createApi, load]);

  const triggerKeywordJob = useCallback(async () => {
    try {
      const api = await createApi();
      await api.post('/api/v1/classifier-tuning/jobs/keywords');
      setTimeout(load, 3000);
    } catch (err) {
      console.error('Failed to trigger keyword mining job', err);
    }
  }, [createApi, load]);

  const revertOverride = useCallback(async (key: string, isGlobal: boolean) => {
    if (!window.confirm(
      `Revert override '${key}'? This restores the IConfiguration / code default ` +
      'and takes effect on the next classifier call.',
    )) return;
    try {
      const api = await createApi();
      await api.post('/api/v1/classifier-tuning/overrides/revert', {
        configKey: key, global: isGlobal,
      });
      load();
    } catch (err) {
      console.error('Revert failed', err);
      window.alert('Revert failed; see console.');
    }
  }, [createApi, load]);

  const setPerPromptModel = useCallback(async (promptKey: string, model: string) => {
    if (!model.trim()) return;
    try {
      const api = await createApi();
      await api.post('/api/v1/classifier-tuning/per-prompt-models', {
        promptKey, model: model.trim(),
      });
      load();
    } catch (err) {
      console.error('Set per-prompt model failed', err);
      window.alert('Failed to set per-prompt model; see console.');
    }
  }, [createApi, load]);

  const deactivateRule = useCallback(async (id: number) => {
    if (!window.confirm('Deactivate this keyword rule? Soft-delete; rule audit trail is preserved.'))
      return;
    try {
      const api = await createApi();
      await api.post(`/api/v1/classifier-tuning/keyword-rules/${id}/deactivate`);
      load();
    } catch (err) {
      console.error('Deactivate failed', err);
    }
  }, [createApi, load]);

  const runEval = useCallback(async () => {
    setEvalRunning(true);
    try {
      const api = await createApi();
      const res = await api.post<EvalSummary>(
        '/api/v1/classifier-tuning/eval/run?sampleSize=200&windowDays=60');
      setEval_(res.data);
      // Refresh history so the new run + the delta-arrow context appear.
      const histRes = await api.get<EvalHistoryEntry[]>(
        '/api/v1/classifier-tuning/eval/history?take=20');
      setEvalHistory(histRes.data ?? []);
    } catch (err) {
      console.error('Eval run failed', err);
    } finally {
      setEvalRunning(false);
    }
  }, [createApi]);

  const triggerCatchallJob = useCallback(async () => {
    try {
      const api = await createApi();
      await api.post('/api/v1/classifier-tuning/jobs/catchall');
      setTimeout(load, 3000);
    } catch (err) {
      console.error('Failed to trigger catchall detection job', err);
    }
  }, [createApi, load]);

  const triggerPromptRewriteJob = useCallback(async () => {
    try {
      const api = await createApi();
      await api.post('/api/v1/classifier-tuning/jobs/prompt-rewrite');
      setTimeout(load, 3000);
    } catch (err) {
      console.error('Failed to trigger prompt rewrite analysis job', err);
    }
  }, [createApi, load]);

  const rejectProposal = useCallback(async (id: number) => {
    const reason = window.prompt('Reason for rejecting this proposal:');
    if (!reason) return;
    setActioningId(id);
    try {
      const api = await createApi();
      await api.post(`/api/v1/classifier-tuning/proposals/${id}/reject`, { reason });
      setProposals(p => p.filter(x => x.id !== id));
    } catch (err) {
      console.error('Failed to reject proposal', err);
    } finally {
      setActioningId(null);
    }
  }, [createApi]);

  const applyProposal = useCallback(async (id: number) => {
    if (!window.confirm(
      'Apply this proposal? This writes a runtime config override that takes ' +
      'effect immediately on the next classifier call. The override can be ' +
      'reverted later via the admin endpoint.',
    )) return;
    setActioningId(id);
    try {
      const api = await createApi();
      const res = await api.post<{
        message: string;
        configKey: string;
        appliedValue: string;
      }>(`/api/v1/classifier-tuning/proposals/${id}/apply`, { notes: null });
      setProposals(p => p.filter(x => x.id !== id));
      window.alert(res.data.message);
    } catch (err: any) {
      const detail = err?.response?.data?.error ?? err?.message ?? 'Apply failed.';
      console.error('Failed to apply proposal', err);
      window.alert(`⚠ ${detail}`);
    } finally {
      setActioningId(null);
    }
  }, [createApi]);

  const snoozeProposal = useCallback(async (id: number, days: number) => {
    setActioningId(id);
    try {
      const api = await createApi();
      const untilUtc = new Date(Date.now() + days * 24 * 3600 * 1000).toISOString();
      await api.post(`/api/v1/classifier-tuning/proposals/${id}/snooze`, { untilUtc });
      setProposals(p => p.filter(x => x.id !== id));
    } catch (err) {
      console.error('Failed to snooze proposal', err);
    } finally {
      setActioningId(null);
    }
  }, [createApi]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <EnginePageShell
      title="Classifier Tuning"
      subtitle="Cascading classifier observability + adaptive learning telemetry"
      accentColor={C.cyan}
    >
      {/* Time-window selector */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <SectionLabel>WINDOW</SectionLabel>
        {[7, 30, 90].map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: `1px solid ${days === d ? C.cyan : C.faint}`,
              background: days === d ? C.cyan + '22' : 'transparent',
              color: days === d ? C.cyan : C.dim,
              fontFamily: F.mono,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {d}d
          </button>
        ))}
        <button
          onClick={runEval}
          disabled={evalRunning}
          style={{
            marginLeft: 'auto',
            padding: '6px 14px',
            borderRadius: 6,
            border: `1px solid ${C.neon}`,
            background: 'transparent',
            color: C.neon,
            fontFamily: F.mono,
            fontSize: 12,
            cursor: evalRunning ? 'wait' : 'pointer',
            opacity: evalRunning ? 0.5 : 1,
          }}
          title="Replay last 200 committed conversations through Tier 1 to measure precision/coverage"
        >
          {evalRunning ? '⧗ Running eval…' : '▶ Run Tier 1 eval'}
        </button>
        <button
          onClick={triggerThresholdJob}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: `1px solid ${C.violet}`,
            background: 'transparent',
            color: C.violet,
            fontFamily: F.mono,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          ↻ Run threshold analysis
        </button>
        <button
          onClick={triggerKeywordJob}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: `1px solid ${C.violet}`,
            background: 'transparent',
            color: C.violet,
            fontFamily: F.mono,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          ↻ Mine keyword rules
        </button>
        <button
          onClick={triggerCatchallJob}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: `1px solid ${C.violet}`,
            background: 'transparent',
            color: C.violet,
            fontFamily: F.mono,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          ↻ Detect catchalls
        </button>
        <button
          onClick={triggerPromptRewriteJob}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: `1px solid ${C.violet}`,
            background: 'transparent',
            color: C.violet,
            fontFamily: F.mono,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          ↻ Mine prompt failures
        </button>
        <button
          onClick={load}
          disabled={loading}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: `1px solid ${C.faint}`,
            background: 'transparent',
            color: C.dim,
            fontFamily: F.mono,
            fontSize: 12,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.4 : 1,
          }}
        >
          {loading ? '…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <GlassPanel>
          <div style={{ color: C.coral, fontFamily: F.display, padding: 12 }}>
            ⚠ {error}
          </div>
        </GlassPanel>
      )}

      {loading && !data && <LoadingShimmer />}

      {data && data.tierHitRates.totalObservations === 0 && (
        <GlassPanel>
          <div style={{ padding: 24, textAlign: 'center', color: C.dim, fontFamily: F.display }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 4 }}>
              No classifier observations yet
            </div>
            <div style={{ fontSize: 13 }}>
              Run an import + classify, then check back. Each conversation classified
              under the cascading classifier (Tier 1 / 2 / 4) lands here.
            </div>
          </div>
        </GlassPanel>
      )}

      {/* Eval — replay-based Tier 1 precision/coverage report */}
      {eval_ && <EvalPanel summary={eval_} history={evalHistory} />}

      {/* Eval history (independent of the live eval; shows persisted runs) */}
      {!eval_ && evalHistory.length > 0 && <EvalHistoryPanel history={evalHistory} />}

      {/* Proposals — surface above metrics so the operator sees pending work first */}
      {proposals.length > 0 && (
        <ProposalsPanel
          proposals={proposals}
          onReject={rejectProposal}
          onSnooze={snoozeProposal}
          onApply={applyProposal}
          actioningId={actioningId}
        />
      )}

      {/* Active overrides + mined keyword rules — what's currently in effect */}
      {(overrides.length > 0 || keywordRules.some(r => r.isActive)) && (
        <ActiveOverridesPanel
          overrides={overrides}
          keywordRules={keywordRules.filter(r => r.isActive)}
          onRevertOverride={revertOverride}
          onDeactivateRule={deactivateRule}
        />
      )}

      {/* Per-prompt-key model overrides */}
      {perPromptModels.length > 0 && (
        <PerPromptModelsPanel
          rows={perPromptModels}
          onSet={setPerPromptModel}
          onRevert={key => revertOverride(`Pipeline:ModelOverride:${key}`, false)}
        />
      )}

      {/* Job status strip — last/next run for the four recurring jobs */}
      {jobStatuses.length > 0 && <JobStatusPanel jobs={jobStatuses} />}

      {data && data.tierHitRates.totalObservations > 0 && (
        <>
          {/* Top-line cost summary */}
          <CostSummary tierHitRates={data.tierHitRates} />

          {/* Tier hit rates */}
          <TierHitRatesPanel tiers={data.tierHitRates.tiers} />

          {/* Per-project accuracy */}
          <PerProjectPanel projects={data.perProject} />

          {/* Catchall detection */}
          <CatchallsPanel
            items={data.catchalls ?? []}
            threshold={5}
          />

          {/* Schema violations */}
          <SchemaViolationsPanel stats={data.schemaViolations} />

          {/* Override rate */}
          <OverrideRatePanel rates={data.overrideRate} />
        </>
      )}
    </EnginePageShell>
  );
}

// ─── Panels ──────────────────────────────────────────────────────────

function CostSummary({ tierHitRates }: { tierHitRates: TierHitRatesResult }) {
  const totalCost = tierHitRates.totalCostUSD;
  const avgCost = tierHitRates.avgCostPerConversationUSD;

  // Project the all-Tier-4 cost we would have paid without the
  // cascading classifier, for comparison.
  const projectedFullCost = tierHitRates.totalObservations * 0.8;
  const savings = projectedFullCost - totalCost;
  const savingsPct = projectedFullCost > 0 ? (savings / projectedFullCost) : 0;

  return (
    <GlassPanel>
      <SectionLabel>COST</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, padding: 16 }}>
        <Metric
          label="Conversations classified"
          value={tierHitRates.totalObservations.toLocaleString()}
          color={C.text}
        />
        <Metric
          label="Total cost"
          value={`$${totalCost.toFixed(2)}`}
          color={C.text}
        />
        <Metric
          label="Avg per conversation"
          value={`$${avgCost.toFixed(4)}`}
          color={avgCost < 0.10 ? C.neon : avgCost < 0.40 ? C.cyan : C.coral}
        />
        <Metric
          label="Estimated savings vs all-Tier-4"
          value={savings > 0 ? `$${savings.toFixed(2)} (${(savingsPct * 100).toFixed(0)}%)` : '—'}
          color={savings > 0 ? C.neon : C.dim}
        />
      </div>
    </GlassPanel>
  );
}

function TierHitRatesPanel({ tiers }: { tiers: TierHitRate[] }) {
  return (
    <GlassPanel>
      <SectionLabel>TIER HIT RATES</SectionLabel>
      <div style={{ padding: 16 }}>
        {tiers.length === 0 ? (
          <div style={{ color: C.dim, fontFamily: F.display }}>No data in window.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: F.display }}>
            <thead>
              <tr style={{ textAlign: 'left', color: C.dim, fontSize: 11, textTransform: 'uppercase' }}>
                <th style={{ padding: '6px 0' }}>Tier</th>
                <th style={{ padding: '6px 0', textAlign: 'right' }}>Count</th>
                <th style={{ padding: '6px 0', textAlign: 'right' }}>%</th>
                <th style={{ padding: '6px 0', textAlign: 'right' }}>Avg cost</th>
                <th style={{ padding: '6px 0', textAlign: 'right' }}>Avg latency</th>
                <th style={{ padding: '6px 0' }}></th>
              </tr>
            </thead>
            <tbody>
              {tiers.map(t => (
                <tr key={t.tier} style={{ borderTop: `1px solid ${C.faint}` }}>
                  <td style={{ padding: '10px 0' }}>
                    <div style={{ color: TIER_COLORS[t.tier] ?? C.text, fontWeight: 600 }}>
                      {TIER_LABELS[t.tier] ?? t.tier}
                    </div>
                    <div style={{ fontSize: 11, color: C.dim }}>{TIER_DESCRIPTIONS[t.tier] ?? ''}</div>
                  </td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: F.mono }}>
                    {t.count.toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: F.mono }}>
                    {(t.percentage * 100).toFixed(1)}%
                  </td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: F.mono }}>
                    ${t.avgCostUSD.toFixed(4)}
                  </td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: F.mono, color: C.dim }}>
                    {t.avgLatencyMs !== null ? `${(t.avgLatencyMs / 1000).toFixed(1)}s` : '—'}
                  </td>
                  <td style={{ padding: '10px 0', width: '40%' }}>
                    {/* Stacked bar visualization */}
                    <div style={{ height: 6, background: C.faint, borderRadius: 3, overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${t.percentage * 100}%`,
                          background: TIER_COLORS[t.tier] ?? C.cyan,
                          transition: 'width 0.4s ease',
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </GlassPanel>
  );
}

function PerProjectPanel({ projects }: { projects: PerProjectAccuracy[] }) {
  return (
    <GlassPanel>
      <SectionLabel>PER-PROJECT ACCEPTANCE</SectionLabel>
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
          Of conversations the human committed under each project, what fraction had the AI's mapping accepted as-is.
          A low rate signals that the project's keyword rules or catalog descriptions need work.
        </div>
        {projects.length === 0 ? (
          <div style={{ color: C.dim, fontFamily: F.display }}>No human commits in window.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: F.display }}>
            <thead>
              <tr style={{ textAlign: 'left', color: C.dim, fontSize: 11, textTransform: 'uppercase' }}>
                <th style={{ padding: '6px 0' }}>Project</th>
                <th style={{ padding: '6px 0', textAlign: 'right' }}>Committed</th>
                <th style={{ padding: '6px 0', textAlign: 'right' }}>Overridden</th>
                <th style={{ padding: '6px 0', textAlign: 'right' }}>Acceptance</th>
                <th style={{ padding: '6px 0' }}></th>
              </tr>
            </thead>
            <tbody>
              {projects.map(p => {
                const pct = p.acceptanceRate * 100;
                const color = pct >= 90 ? C.neon : pct >= 75 ? C.cyan : pct >= 50 ? C.amber : C.coral;
                return (
                  <tr key={p.projectId ?? 'unknown'} style={{ borderTop: `1px solid ${C.faint}` }}>
                    <td style={{ padding: '10px 0', color: C.text, fontWeight: 600 }}>
                      {p.projectName ?? '(no project)'}
                    </td>
                    <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: F.mono }}>
                      {p.committedCount}
                    </td>
                    <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: F.mono, color: C.dim }}>
                      {p.overriddenCount}
                    </td>
                    <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: F.mono, color, fontWeight: 600 }}>
                      {pct.toFixed(1)}%
                    </td>
                    <td style={{ padding: '10px 0', width: '30%' }}>
                      <div style={{ height: 6, background: C.faint, borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: color }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </GlassPanel>
  );
}

function CatchallsPanel({ items, threshold }: { items: CatchallWorkItem[]; threshold: number }) {
  return (
    <GlassPanel>
      <SectionLabel>CATCHALL DETECTION</SectionLabel>
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
          Work items that absorbed ≥ {threshold} conversations in the window. Concentration is a smell —
          either a real cluster (split it) or the classifier forcing marginal matches onto a "general" bucket.
        </div>
        {items.length === 0 ? (
          <div style={{ color: C.neon, fontFamily: F.display }}>✓ No catchall convergence detected.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: F.display }}>
            <thead>
              <tr style={{ textAlign: 'left', color: C.dim, fontSize: 11, textTransform: 'uppercase' }}>
                <th style={{ padding: '6px 0' }}>Work item</th>
                <th style={{ padding: '6px 0' }}>Project</th>
                <th style={{ padding: '6px 0', textAlign: 'right' }}>Conv. count</th>
                <th style={{ padding: '6px 0', textAlign: 'right' }}>Avg conf.</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.workItemId} style={{ borderTop: `1px solid ${C.faint}` }}>
                  <td style={{ padding: '10px 0', color: C.text }}>
                    {item.workItemTitle}
                    <span style={{ color: C.dim, marginLeft: 6 }}>#{item.workItemId}</span>
                  </td>
                  <td style={{ padding: '10px 0', color: C.dim }}>{item.projectName}</td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: F.mono, color: item.conversationCount > 15 ? C.coral : C.amber, fontWeight: 600 }}>
                    {item.conversationCount}
                  </td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: F.mono, color: C.dim }}>
                    {(item.avgConfidence * 100).toFixed(0)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </GlassPanel>
  );
}

function SchemaViolationsPanel({ stats }: { stats: SchemaViolationStat }) {
  const ratePct = stats.schemaViolationRate * 100;
  const color = ratePct < 1 ? C.neon : ratePct < 5 ? C.cyan : ratePct < 15 ? C.amber : C.coral;
  return (
    <GlassPanel>
      <SectionLabel>TIER 4 SCHEMA VIOLATIONS</SectionLabel>
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
          Rate at which the v5 pipeline returned with a parser failure. High rate
          on long conversations (&gt;50 messages) points to truncated outputs;
          uniform high rate points to soft-constraint JSON enforcement (upgrade to json_schema mode).
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <Metric label="Tier 4 runs" value={stats.tier4TotalRuns.toLocaleString()} color={C.text} />
          <Metric label="Violations" value={stats.tier4SchemaViolations.toLocaleString()} color={color} />
          <Metric label="Rate" value={`${ratePct.toFixed(2)}%`} color={color} />
          <Metric
            label="Long vs short conversation"
            value={`${stats.longConversationViolations} / ${stats.shortConversationViolations}`}
            color={C.dim}
          />
        </div>
      </div>
    </GlassPanel>
  );
}

function OverrideRatePanel({ rates }: { rates: HumanOverrideRate }) {
  const mappingPct = rates.overrideRate * 100;
  const rdPct = rates.rdOverrideRate * 100;
  const mappingColor = mappingPct < 10 ? C.neon : mappingPct < 25 ? C.cyan : mappingPct < 50 ? C.amber : C.coral;
  const rdColor = rdPct < 5 ? C.neon : rdPct < 15 ? C.cyan : rdPct < 30 ? C.amber : C.coral;
  return (
    <GlassPanel>
      <SectionLabel>HUMAN OVERRIDE RATE</SectionLabel>
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
          Of conversations the human committed, what fraction had the AI's mapping or R&D verdict overridden.
          The single most important accuracy signal — drives every downstream proposal.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <Metric label="Total committed" value={rates.totalCommitted.toLocaleString()} color={C.text} />
          <Metric label="Mapping override rate" value={`${mappingPct.toFixed(1)}%`} color={mappingColor} />
          <Metric label="R&D verdict override rate" value={`${rdPct.toFixed(1)}%`} color={rdColor} />
          <Metric
            label="Implied AI mapping accuracy"
            value={`${(100 - mappingPct).toFixed(1)}%`}
            color={mappingColor}
          />
        </div>
      </div>
    </GlassPanel>
  );
}

// ─── Proposals (Phase C) ──────────────────────────────────────────────

function ProposalsPanel({
  proposals,
  onReject,
  onSnooze,
  onApply,
  actioningId,
}: {
  proposals: ProposalSummary[];
  onReject: (id: number) => void;
  onSnooze: (id: number, days: number) => void;
  onApply: (id: number) => void;
  actioningId: number | null;
}) {
  const typeLabels: Record<string, string> = {
    threshold_tweak: '⚙️ Threshold tweak',
    keyword_rule: '🔍 Keyword rule',
    catchall_split: '🪣 Catchall split',
    prompt_rewrite: '✍️ Prompt rewrite',
    prompt_rewrite_rd: '🧪 Prompt rewrite (R&D)',
    model_choice: '🧠 Model choice',
  };

  return (
    <GlassPanel>
      <SectionLabel>PENDING PROPOSALS · {proposals.length}</SectionLabel>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 12, color: C.dim }}>
          The adaptive learning loop has identified {proposals.length} change{proposals.length === 1 ? '' : 's'}
          {' '}that may improve classifier accuracy or reduce cost. Review the evidence,
          then Apply, Reject, or Snooze.
        </div>
        {proposals.map(p => (
          <div
            key={p.id}
            style={{
              border: `1px solid ${C.faint}`,
              borderRadius: 8,
              padding: 14,
              background: C.surface,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, color: C.violet, fontFamily: F.mono }}>
                    {typeLabels[p.proposalType] ?? p.proposalType}
                  </span>
                  <span style={{ fontSize: 11, color: C.dim, fontFamily: F.mono }}>
                    · {new Date(p.generatedAt).toLocaleDateString()}
                  </span>
                  {p.generatedByJob && (
                    <span style={{ fontSize: 10, color: C.dim, fontFamily: F.mono }}>
                      · {p.generatedByJob}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: F.display, marginBottom: 4 }}>
                  {p.title}
                </div>
                {p.projectedImpact && (
                  <div style={{ fontSize: 12, color: C.neon, fontFamily: F.mono, marginBottom: 6 }}>
                    {p.projectedImpact}
                  </div>
                )}
                <div style={{ fontSize: 12, color: C.muted, fontFamily: F.display, lineHeight: 1.4 }}>
                  {p.description}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 110 }}>
                <button
                  onClick={() => onApply(p.id)}
                  disabled={actioningId === p.id}
                  title="Apply this proposal — writes a runtime config override"
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: `1px solid ${C.neon}`,
                    background: 'transparent',
                    color: C.neon,
                    fontFamily: F.mono,
                    fontSize: 11,
                    cursor: actioningId === p.id ? 'wait' : 'pointer',
                    opacity: actioningId === p.id ? 0.5 : 1,
                  }}
                >
                  Apply
                </button>
                <button
                  onClick={() => onSnooze(p.id, 30)}
                  disabled={actioningId === p.id}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: `1px solid ${C.amber}`,
                    background: 'transparent',
                    color: C.amber,
                    fontFamily: F.mono,
                    fontSize: 11,
                    cursor: 'pointer',
                  }}
                >
                  Snooze 30d
                </button>
                <button
                  onClick={() => onReject(p.id)}
                  disabled={actioningId === p.id}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: `1px solid ${C.coral}`,
                    background: 'transparent',
                    color: C.coral,
                    fontFamily: F.mono,
                    fontSize: 11,
                    cursor: 'pointer',
                  }}
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </GlassPanel>
  );
}

// ─── Tiny shared metric ───────────────────────────────────────────────

function Metric({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: C.dim, fontFamily: F.display, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: F.mono, marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

// ─── Eval (Tier 1 replay) ────────────────────────────────────────────

function EvalPanel({ summary, history }: { summary: EvalSummary; history: EvalHistoryEntry[] }) {
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
  // The most recent persisted run is the one we just inserted; compare
  // against the second most recent (the previous baseline).
  const previous = history.length >= 2 ? history[1] : null;
  const delta = (cur: number, prev: number | undefined) => {
    if (prev === undefined) return null;
    const d = cur - prev;
    if (Math.abs(d) < 0.0005) return { sign: '·', text: '0.0%', color: C.dim };
    if (d > 0) return { sign: '▲', text: `+${(d * 100).toFixed(1)}%`, color: C.neon };
    return { sign: '▼', text: `${(d * 100).toFixed(1)}%`, color: C.coral };
  };
  if (summary.corpusSize === 0) {
    return (
      <GlassPanel accent={C.amber}>
        <SectionLabel color={C.amber}>
          TIER 1 EVAL · 0 obs · {new Date(summary.ranAtUtc).toLocaleTimeString()}
        </SectionLabel>
        <div style={{ padding: 16, fontSize: 13, color: C.text, fontFamily: F.display, lineHeight: 1.5 }}>
          <div style={{ marginBottom: 8 }}>
            <strong>No committed observations to evaluate against.</strong>
          </div>
          <div style={{ color: C.muted, fontSize: 12 }}>
            The eval harness measures Tier 1 precision against ground truth — that means
            it needs conversations the operator has already <em>Committed</em> (turned into
            Sessions). You have classifier observations in the system, but none have been
            committed yet.
          </div>
          <div style={{ color: C.dim, fontSize: 12, marginTop: 10, fontFamily: F.mono }}>
            Next step: open the import page → set filter to <strong style={{ color: C.cyan }}>Mapped</strong>
            {' '}→ click <strong style={{ color: C.neon }}>✓ Commit All Mapped</strong> on rows you've
            verified. Then re-run the eval.
          </div>
        </div>
      </GlassPanel>
    );
  }

  return (
    <GlassPanel>
      <SectionLabel>
        TIER 1 EVAL · {summary.corpusSize} obs · threshold {summary.currentBypassThreshold.toFixed(2)} ·
        {' '}{new Date(summary.ranAtUtc).toLocaleTimeString()}
        {previous && (
          <span style={{ color: C.dim, marginLeft: 8 }}>
            (vs {new Date(previous.ranAtUtc).toLocaleString()})
          </span>
        )}
      </SectionLabel>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <MetricWithDelta
            label="Confident precision"
            value={pct(summary.tier1ConfidentPrecision)}
            color={summary.tier1ConfidentPrecision >= 0.95 ? C.neon
                 : summary.tier1ConfidentPrecision >= 0.85 ? C.amber : C.coral}
            delta={delta(summary.tier1ConfidentPrecision, previous?.tier1ConfidentPrecision)}
          />
          <MetricWithDelta
            label="Confident coverage"
            value={pct(summary.tier1ConfidentCoverage)}
            color={C.cyan}
            delta={delta(summary.tier1ConfidentCoverage, previous?.tier1ConfidentCoverage)}
          />
          <MetricWithDelta
            label="Any-verdict precision"
            value={pct(summary.tier1AnyPrecision)}
            color={C.violet}
            delta={delta(summary.tier1AnyPrecision, previous?.tier1AnyPrecision)}
          />
          <MetricWithDelta
            label="Any-verdict recall"
            value={pct(summary.tier1AnyRecall)}
            color={C.violet}
            delta={delta(summary.tier1AnyRecall, previous?.tier1AnyRecall)}
          />
        </div>

        <div style={{ fontSize: 12, color: C.dim, fontFamily: F.mono }}>
          {summary.tier1ConfidentCorrect}/{summary.tier1ConfidentVerdicts} correct at confidence ≥
          {' '}{summary.currentBypassThreshold.toFixed(2)} →
          {' '}{summary.corpusSize - summary.tier1ConfidentVerdicts} would escalate to Tier 2/4.
        </div>

        {summary.byProject.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, marginBottom: 6 }}>
              PER-PROJECT (committed corpus)
            </div>
            <table style={{ width: '100%', fontFamily: F.mono, fontSize: 11, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: C.dim, textAlign: 'left' }}>
                  <th style={{ padding: '4px 8px' }}>Project</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>n</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Precision</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Coverage</th>
                </tr>
              </thead>
              <tbody>
                {summary.byProject.map(p => (
                  <tr key={p.projectId} style={{ borderTop: `1px solid ${C.faint}` }}>
                    <td style={{ padding: '4px 8px', color: C.text }}>{p.projectName}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: C.muted }}>{p.corpusSize}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right',
                                 color: p.precisionAtConfident >= 0.95 ? C.neon
                                      : p.precisionAtConfident >= 0.85 ? C.amber : C.coral }}>
                      {pct(p.precisionAtConfident)}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: C.cyan }}>
                      {pct(p.coverageAtConfident)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {summary.sampleMisses.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, marginBottom: 6 }}>
              CONFIDENT-BUT-WRONG · {summary.sampleMisses.length} surfaced (Tier 1 said yes with high
              confidence; ground truth disagrees — these are the highest-cost misses)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflow: 'auto' }}>
              {summary.sampleMisses.map(m => (
                <div
                  key={m.observationId}
                  style={{
                    fontSize: 11,
                    fontFamily: F.mono,
                    color: C.muted,
                    padding: 8,
                    border: `1px solid ${C.faint}`,
                    borderRadius: 4,
                    background: C.surface,
                  }}
                >
                  <div style={{ color: C.text, marginBottom: 2 }}>{m.conversationTitle}</div>
                  <div>
                    <span style={{ color: C.coral }}>predicted</span>: WI #{m.tier1WorkItemId} (
                    {(m.tier1Confidence * 100).toFixed(0)}%, {m.tierExitedAt}){'  '}
                    <span style={{ color: C.neon }}>actual</span>: WI #{m.humanWorkItemId}
                  </div>
                  <div style={{ color: C.dim, marginTop: 2 }}>{m.reasoning}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {summary.rdVerdict && summary.rdVerdict.totalEvaluated > 0 && (
          <RDVerdictBlock metrics={summary.rdVerdict} />
        )}
      </div>
    </GlassPanel>
  );
}

function RDVerdictBlock({ metrics }: { metrics: EvalRDVerdictMetrics }) {
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
  return (
    <div style={{
      marginTop: 4,
      paddingTop: 14,
      borderTop: `1px solid ${C.faint}`,
    }}>
      <div style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, marginBottom: 8 }}>
        TIER 4 R&D VERDICT · {metrics.totalEvaluated} evaluated · positive class = "qualifies"
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
        <Metric
          label="Precision"
          value={pct(metrics.precision)}
          color={metrics.precision >= 0.90 ? C.neon
               : metrics.precision >= 0.75 ? C.amber : C.coral}
        />
        <Metric
          label="Recall"
          value={pct(metrics.recall)}
          color={metrics.recall >= 0.90 ? C.neon
               : metrics.recall >= 0.75 ? C.amber : C.coral}
        />
        <Metric label="F1" value={pct(metrics.f1)} color={C.cyan} />
        <Metric
          label="False neg / pos"
          value={`${metrics.falseNegative} / ${metrics.falsePositive}`}
          color={metrics.falseNegative > 0 ? C.coral : C.dim}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, fontSize: 10, fontFamily: F.mono, marginBottom: 12 }}>
        <div style={{ color: C.neon }}>TP {metrics.truePositive} (AI yes / human yes)</div>
        <div style={{ color: C.neon }}>TN {metrics.trueNegative} (AI no / human no)</div>
        <div style={{ color: C.coral }}>FP {metrics.falsePositive} (AI yes / human no)</div>
        <div style={{ color: C.coral }}>FN {metrics.falseNegative} (AI no / human yes)</div>
      </div>

      {metrics.sampleFalseNegatives.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: C.coral, fontFamily: F.mono, marginBottom: 4 }}>
            FALSE NEGATIVES · AI missed these qualifying conversations (highest-cost direction)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflow: 'auto' }}>
            {metrics.sampleFalseNegatives.map(s => (
              <div key={s.observationId} style={{
                fontSize: 11, fontFamily: F.mono, color: C.muted,
                padding: 6, border: `1px solid ${C.faint}`, borderRadius: 4,
                background: C.surface,
              }}>
                <div style={{ color: C.text }}>{s.conversationTitle}</div>
                <div style={{ color: C.dim, fontSize: 10 }}>
                  obs #{s.observationId} · {s.messageCount} msgs · {s.durationMinutes ?? '?'} min
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {metrics.sampleFalsePositives.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: C.amber, fontFamily: F.mono, marginBottom: 4 }}>
            FALSE POSITIVES · AI over-claimed (audit risk)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflow: 'auto' }}>
            {metrics.sampleFalsePositives.map(s => (
              <div key={s.observationId} style={{
                fontSize: 11, fontFamily: F.mono, color: C.muted,
                padding: 6, border: `1px solid ${C.faint}`, borderRadius: 4,
                background: C.surface,
              }}>
                <div style={{ color: C.text }}>{s.conversationTitle}</div>
                <div style={{ color: C.dim, fontSize: 10 }}>
                  obs #{s.observationId} · {s.messageCount} msgs · {s.durationMinutes ?? '?'} min
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Active overrides + applied keyword rules audit panel ────────────

function ActiveOverridesPanel({
  overrides,
  keywordRules,
  onRevertOverride,
  onDeactivateRule,
}: {
  overrides: ConfigOverrideRow[];
  keywordRules: KeywordRuleRow[];
  onRevertOverride: (key: string, isGlobal: boolean) => void;
  onDeactivateRule: (id: number) => void;
}) {
  return (
    <GlassPanel>
      <SectionLabel>
        ACTIVE OVERRIDES · {overrides.length} config · {keywordRules.length} keyword rule
        {keywordRules.length === 1 ? '' : 's'}
      </SectionLabel>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontSize: 12, color: C.dim, fontFamily: F.display }}>
          Audit trail for the adaptive-learning Apply path. Each row was installed by an
          accepted proposal (or, for manual overrides, written directly via the admin
          endpoint). Revert restores the previous state.
        </div>

        {overrides.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, marginBottom: 6 }}>
              SYSTEM CONFIG OVERRIDES
            </div>
            <table style={{ width: '100%', fontFamily: F.mono, fontSize: 11, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: C.dim, textAlign: 'left' }}>
                  <th style={{ padding: '4px 8px' }}>Scope</th>
                  <th style={{ padding: '4px 8px' }}>Key</th>
                  <th style={{ padding: '4px 8px' }}>Value</th>
                  <th style={{ padding: '4px 8px' }}>Type</th>
                  <th style={{ padding: '4px 8px' }}>From proposal</th>
                  <th style={{ padding: '4px 8px' }}>Updated</th>
                  <th style={{ padding: '4px 8px' }}></th>
                </tr>
              </thead>
              <tbody>
                {overrides.map(o => (
                  <tr key={o.id} style={{ borderTop: `1px solid ${C.faint}` }}>
                    <td style={{ padding: '4px 8px', color: o.tenantId === null ? C.amber : C.cyan }}>
                      {o.tenantId === null ? 'global' : `tenant ${o.tenantId}`}
                    </td>
                    <td style={{ padding: '4px 8px', color: C.text }}>{o.configKey}</td>
                    <td style={{ padding: '4px 8px', color: C.muted, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={o.configValue}>
                      {o.configValue}
                    </td>
                    <td style={{ padding: '4px 8px', color: C.dim }}>{o.valueType}</td>
                    <td style={{ padding: '4px 8px', color: C.dim }}>
                      {o.appliedFromProposalId ? `#${o.appliedFromProposalId}` : '—'}
                    </td>
                    <td style={{ padding: '4px 8px', color: C.dim }}>
                      {new Date(o.updatedAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      <button
                        onClick={() => onRevertOverride(o.configKey, o.tenantId === null)}
                        style={{
                          padding: '2px 8px',
                          borderRadius: 4,
                          border: `1px solid ${C.coral}`,
                          background: 'transparent',
                          color: C.coral,
                          fontFamily: F.mono,
                          fontSize: 10,
                          cursor: 'pointer',
                        }}
                      >
                        Revert
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {keywordRules.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, marginBottom: 6 }}>
              KEYWORD RULES (TenantKeywordRules)
            </div>
            <table style={{ width: '100%', fontFamily: F.mono, fontSize: 11, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: C.dim, textAlign: 'left' }}>
                  <th style={{ padding: '4px 8px' }}>Source</th>
                  <th style={{ padding: '4px 8px' }}>Project</th>
                  <th style={{ padding: '4px 8px' }}>Pattern</th>
                  <th style={{ padding: '4px 8px' }}>Description</th>
                  <th style={{ padding: '4px 8px' }}>From proposal</th>
                  <th style={{ padding: '4px 8px' }}>Created</th>
                  <th style={{ padding: '4px 8px' }}></th>
                </tr>
              </thead>
              <tbody>
                {keywordRules.map(r => (
                  <tr key={r.id} style={{ borderTop: `1px solid ${C.faint}` }}>
                    <td style={{ padding: '4px 8px',
                                 color: r.source === 'mined' ? C.violet
                                      : r.source === 'manual' ? C.cyan : C.dim }}>
                      {r.source}
                    </td>
                    <td style={{ padding: '4px 8px', color: C.muted }}>#{r.projectId}</td>
                    <td style={{ padding: '4px 8px', color: C.text, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={r.pattern}>
                      {r.pattern}
                    </td>
                    <td style={{ padding: '4px 8px', color: C.muted, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={r.description}>
                      {r.description}
                    </td>
                    <td style={{ padding: '4px 8px', color: C.dim }}>
                      {r.appliedFromProposalId ? `#${r.appliedFromProposalId}` : '—'}
                    </td>
                    <td style={{ padding: '4px 8px', color: C.dim }}>
                      {new Date(r.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      <button
                        onClick={() => onDeactivateRule(r.id)}
                        style={{
                          padding: '2px 8px',
                          borderRadius: 4,
                          border: `1px solid ${C.coral}`,
                          background: 'transparent',
                          color: C.coral,
                          fontFamily: F.mono,
                          fontSize: 10,
                          cursor: 'pointer',
                        }}
                      >
                        Deactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </GlassPanel>
  );
}

// ─── Metric with optional delta arrow ────────────────────────────────

function MetricWithDelta({
  label, value, color, delta,
}: {
  label: string;
  value: string;
  color: string;
  delta: { sign: string; text: string; color: string } | null;
}) {
  return (
    <div>
      <div style={{ fontSize: 11, color: C.dim, fontFamily: F.display, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: F.mono }}>
          {value}
        </div>
        {delta && (
          <div style={{ fontSize: 11, color: delta.color, fontFamily: F.mono }}>
            {delta.sign} {delta.text}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Eval history (no current run loaded) ────────────────────────────

function EvalHistoryPanel({ history }: { history: EvalHistoryEntry[] }) {
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
  return (
    <GlassPanel>
      <SectionLabel>EVAL HISTORY · {history.length} run{history.length === 1 ? '' : 's'}</SectionLabel>
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 12, color: C.dim, fontFamily: F.display, marginBottom: 10 }}>
          Persisted Tier 1 eval results. Run ▶ Tier 1 eval to add a new row; the panel above
          will show delta-vs-previous on the headline metrics.
        </div>
        <table style={{ width: '100%', fontFamily: F.mono, fontSize: 11, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: C.dim, textAlign: 'left' }}>
              <th style={{ padding: '4px 8px' }}>When</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Corpus</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Threshold</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Confident P</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Confident Cov</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Any P</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Any R</th>
            </tr>
          </thead>
          <tbody>
            {history.map(h => (
              <tr key={h.id} style={{ borderTop: `1px solid ${C.faint}` }}>
                <td style={{ padding: '4px 8px', color: C.text }}>
                  {new Date(h.ranAtUtc).toLocaleString()}
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'right', color: C.muted }}>{h.corpusSize}</td>
                <td style={{ padding: '4px 8px', textAlign: 'right', color: C.muted }}>{h.bypassThreshold.toFixed(2)}</td>
                <td style={{ padding: '4px 8px', textAlign: 'right',
                             color: h.tier1ConfidentPrecision >= 0.95 ? C.neon
                                  : h.tier1ConfidentPrecision >= 0.85 ? C.amber : C.coral }}>
                  {pct(h.tier1ConfidentPrecision)}
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'right', color: C.cyan }}>
                  {pct(h.tier1ConfidentCoverage)}
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'right', color: C.violet }}>
                  {pct(h.tier1AnyPrecision)}
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'right', color: C.violet }}>
                  {pct(h.tier1AnyRecall)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GlassPanel>
  );
}

// ─── Per-prompt-key model overrides ──────────────────────────────────

function PerPromptModelsPanel({
  rows,
  onSet,
  onRevert,
}: {
  rows: PerPromptModelRow[];
  onSet: (promptKey: string, model: string) => void;
  onRevert: (promptKey: string) => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const updateDraft = (k: string, v: string) =>
    setDrafts(d => ({ ...d, [k]: v }));
  return (
    <GlassPanel>
      <SectionLabel>
        PER-PROMPT MODELS · {rows.length} prompts · {rows.filter(r => r.isOverride).length} overridden
      </SectionLabel>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 12, color: C.dim, fontFamily: F.display }}>
          Pin a specific model to one pipeline step. Tenant-scoped, takes precedence over
          the per-task-type assignment. Use cheaper models on triage / decision / complexity
          steps to keep flagship models for R&amp;D narrative and the highest-stakes calls.
        </div>
        <table style={{ width: '100%', fontFamily: F.mono, fontSize: 11, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: C.dim, textAlign: 'left' }}>
              <th style={{ padding: '4px 8px' }}>Prompt</th>
              <th style={{ padding: '4px 8px' }}>Default model</th>
              <th style={{ padding: '4px 8px' }}>Active model</th>
              <th style={{ padding: '4px 8px' }}>Set override</th>
              <th style={{ padding: '4px 8px' }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.promptKey} style={{ borderTop: `1px solid ${C.faint}` }}>
                <td style={{ padding: '4px 8px', color: C.text }}>
                  <div>{r.displayName}</div>
                  <div style={{ color: C.dim, fontSize: 10 }}>{r.promptKey}</div>
                </td>
                <td style={{ padding: '4px 8px', color: C.muted }}>{r.defaultModel}</td>
                <td style={{ padding: '4px 8px', color: r.isOverride ? C.violet : C.dim }}>
                  {r.overrideModel ?? r.defaultModel}
                  {r.isOverride && <span style={{ color: C.dim, marginLeft: 6 }}>(override)</span>}
                </td>
                <td style={{ padding: '4px 8px' }}>
                  <input
                    type="text"
                    placeholder={r.overrideModel ?? 'e.g. gpt-4o-mini'}
                    value={drafts[r.promptKey] ?? ''}
                    onChange={e => updateDraft(r.promptKey, e.target.value)}
                    style={{
                      width: 200,
                      padding: '3px 6px',
                      border: `1px solid ${C.faint}`,
                      borderRadius: 4,
                      background: C.surface,
                      color: C.text,
                      fontFamily: F.mono,
                      fontSize: 11,
                    }}
                  />
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    onClick={() => {
                      const m = drafts[r.promptKey] ?? '';
                      if (m.trim()) {
                        onSet(r.promptKey, m);
                        updateDraft(r.promptKey, '');
                      }
                    }}
                    disabled={!drafts[r.promptKey]?.trim()}
                    style={{
                      padding: '2px 8px',
                      borderRadius: 4,
                      border: `1px solid ${C.neon}`,
                      background: 'transparent',
                      color: C.neon,
                      fontFamily: F.mono,
                      fontSize: 10,
                      cursor: drafts[r.promptKey]?.trim() ? 'pointer' : 'not-allowed',
                      opacity: drafts[r.promptKey]?.trim() ? 1 : 0.4,
                      marginRight: 4,
                    }}
                  >
                    Set
                  </button>
                  {r.isOverride && (
                    <button
                      onClick={() => onRevert(r.promptKey)}
                      style={{
                        padding: '2px 8px',
                        borderRadius: 4,
                        border: `1px solid ${C.coral}`,
                        background: 'transparent',
                        color: C.coral,
                        fontFamily: F.mono,
                        fontSize: 10,
                        cursor: 'pointer',
                      }}
                    >
                      Revert
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GlassPanel>
  );
}

// ─── Recurring job status strip ──────────────────────────────────────

function JobStatusPanel({ jobs }: { jobs: JobStatusRow[] }) {
  const labels: Record<string, string> = {
    'classifier-threshold-tuning':         'Threshold tuning',
    'classifier-keyword-mining':           'Keyword mining',
    'classifier-catchall-detection':       'Catchall detection',
    'classifier-prompt-rewrite-analysis':  'Prompt-rewrite analysis',
  };
  const fmt = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };
  return (
    <GlassPanel>
      <SectionLabel>RECURRING JOBS · {jobs.length}</SectionLabel>
      <div style={{ padding: 16 }}>
        <table style={{ width: '100%', fontFamily: F.mono, fontSize: 11, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: C.dim, textAlign: 'left' }}>
              <th style={{ padding: '4px 8px' }}>Job</th>
              <th style={{ padding: '4px 8px' }}>Cron (UTC)</th>
              <th style={{ padding: '4px 8px' }}>Last execution</th>
              <th style={{ padding: '4px 8px' }}>Next execution</th>
              <th style={{ padding: '4px 8px' }}>Queue</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map(j => (
              <tr key={j.jobId} style={{ borderTop: `1px solid ${C.faint}` }}>
                <td style={{ padding: '4px 8px', color: C.text }}>
                  {labels[j.jobId] ?? j.jobId}
                </td>
                <td style={{ padding: '4px 8px', color: C.muted }}>{j.cron ?? '—'}</td>
                <td style={{ padding: '4px 8px', color: j.lastExecution ? C.cyan : C.dim }}>
                  {fmt(j.lastExecution)}
                </td>
                <td style={{ padding: '4px 8px', color: j.nextExecution ? C.violet : C.dim }}>
                  {fmt(j.nextExecution)}
                </td>
                <td style={{ padding: '4px 8px', color: C.dim }}>{j.queue ?? 'default'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: C.dim, fontFamily: F.display, marginTop: 10 }}>
          Times reported in UTC by Hangfire. Use the toolbar triggers above for on-demand runs;
          this panel reflects the recurring schedule only.
        </div>
      </div>
    </GlassPanel>
  );
}
